'use client';
import type { ImageType } from '@eliadmin/core/src/api/api-types';
import {
  CategoryCard,
  CategoryCardImage,
  CategoryCardName,
} from '@eliadmin/core/src/components/categories/category-card';
import { SubcategoriesCards } from '@eliadmin/core/src/components/categories/subcategories-cards';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import React, { useState } from 'react';
import { FiChevronLeft, FiHome } from 'react-icons/fi';
import { SubcategoriesList } from './subcategories-list';
import { Heading } from '../shared/heading';

type BreadcrumbsProps = {
  categoryStack: any[];
  onBreadcrumbClick: (index: number) => void;
};

const Breadcrumbs = ({ categoryStack, onBreadcrumbClick }: BreadcrumbsProps) => {
  if (!categoryStack.length) {
    return null;
  }

  return (
    <nav className="breadcrumb ml-1 px-3 sm:px-0">
      <ul className="flex items-center space-x-2 py-2">
        <li>
          <p
            className="cursor-pointer text-primary hover:underline"
            onClick={() => onBreadcrumbClick(0)}
          >
            <FiHome />
          </p>
        </li>
        {categoryStack.map((category: any, index: number) => (
          <li key={category.slug} className="flex items-center">
            <span className="ml-[2px] mr-1 cursor-default text-primary">/</span>
            <p
              className={cn('line-clamp-1 cursor-pointer text-primary', {
                'cursor-default text-foreground': index === categoryStack.length - 1,
                'hover:underline': index !== categoryStack.length - 1,
              })}
              onClick={() => onBreadcrumbClick(index + 1)}
            >
              {category.name}
            </p>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export type Category = {
  data?: Category[];
  childs: Category[];
  name: string;
  description: string;
  short_description: string;
  slug: string;
  icon?: string;
  image?: ImageType;
};

type CategoriesPageNewProps = {
  categories: Category[];
};

export const CategoriesPageNew = ({ categories }: CategoriesPageNewProps) => {
  const [categoryStack, setCategoryStack] = useState<Category[]>([]);

  const { t } = useTranslations();

  const handleCategoryClick = (category: Category) => {
    setCategoryStack((prevStack: Category[]) => [...prevStack, category]);
    window.scrollTo(0, 0);
  };

  const handleBackClick = () => {
    setCategoryStack(prevStack => prevStack.slice(0, -1));
    window.scrollTo(0, 0);
  };

  const handleBreadcrumbClick = (index: number) => {
    setCategoryStack(categoryStack.slice(0, index));
    window.scrollTo(0, 0);
  };

  const currentCategory = categoryStack.length > 0 ? categoryStack[categoryStack.length - 1] : null;
  const categoriesToDisplay = currentCategory ? currentCategory.childs : categories;

  return (
    <section className={cn('custom-section', 'py-16 sm:py-20 md:py-24 lg:py-32 xl:py-40')}>
      <div className={cn('custom-container', 'flex flex-col items-start justify-start gap-12')}>
        <Heading as="h1" title={t('text176')} />
        <div
          className={cn(
            'custom-container',
            'grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 md:grid-cols-3 lg:grid-cols-4',
          )}
        >
          {categories.map(category => (
            <CategoryCard key={category.slug} category={category as any} detailedStyles />
          ))}
        </div>
      </div>
    </section>
  );
};
